/**
 * Person → pedigrees listing + family-less pedigree creation (PTV2-1148).
 *
 * The patient-record Pedigree section lists every pedigree a person is a member of — including
 * family-less (floating) pedigrees, which the `/persons/{guid}/families` listing cannot reach. That
 * discovery endpoint (`GET /api/v1/persons/{guid}/pedigrees`) is new and not yet in the generated
 * OpenAPI schema, so this module hand-types the wire shapes (the families slice's documented drift
 * pattern) until `pnpm generate:api` catches up.
 *
 * "New pedigree" creates a floating pedigree via `POST /api/v2/pedigree` (a different API version
 * than the app's `/api/v1` base), so the create call computes the v2 URL and goes through the
 * pedigree editor's auth-carrying fetch rather than the v1 `http` client.
 */
import { queryOptions } from '@tanstack/react-query';

import { http } from '@/api/http';
import { familiesApi, type FamilyDto } from '@/features/families/api';

import { createPtv2Fetch } from './ptv2Fetch';

/** One row of `GET /persons/{guid}/pedigrees` — matches the BE `PersonPedigreeSummaryDTO`. */
export interface PersonPedigreeSummaryDto {
  readonly pedigree_guid: string;
  readonly family_guid?: string | null;
  readonly family_name?: string | null;
  readonly proband_guid?: string | null;
  readonly proband_name?: string | null;
  /** The proband's last name; drives the per-tile "Create family" affordance (PTV2-1148). */
  readonly proband_last_name?: string | null;
  /** The pedigree's own name, independent of its family; null when unnamed. */
  readonly pedigree_name?: string | null;
  readonly node_count: number;
  /** How many of the pedigree's members are registered patients — drives "shared with N patients". */
  readonly linked_patient_count: number;
  /** Source type of the pedigree (`Source.type`); null when no source is recorded. */
  readonly source_type?: string | null;
  /** Source label of the pedigree (`Source.label`); the tile's "Source:" value. */
  readonly source_label?: string | null;
  /** When source_type is QUESTIONNAIRE, the originating questionnaire-request guid — lets the
   *  "Source:" value link to the request. Null for ordinary pedigrees. */
  readonly source_questionnaire_request_guid?: string | null;
  /** Guid of the pedigree's creator (`@CreatedBy`); resolved to a display name on the tile. */
  readonly author?: string | null;
  /** Guid of the user who last modified the pedigree (`@LastModifiedBy`); resolved on the tile. */
  readonly modified_by?: string | null;
  /** ISO-8601 timestamp of the pedigree's last modification (`@LastModifiedDate`). */
  readonly modified_on?: string | null;
}

/** Spring `Page<PersonPedigreeSummary>` envelope. */
export interface PagePersonPedigreeSummaryDto {
  readonly content?: readonly PersonPedigreeSummaryDto[];
  readonly totalElements?: number;
  readonly totalPages?: number;
  readonly number?: number;
  readonly empty?: boolean;
}

const STALE_TIME = 60_000;

/**
 * GET /persons/{guid}/pedigrees — the pedigrees the person is a member of (floating ones included).
 * Empty page (200) when the person exists but is in no pedigree; 404 when the person guid is
 * unknown (the parent page already 404s on an unknown patient, so callers can treat that as empty).
 */
async function listPersonPedigrees(
  personGuid: string,
  signal: AbortSignal,
): Promise<readonly PersonPedigreeSummaryDto[]> {
  const page = await http.get<PagePersonPedigreeSummaryDto>(
    `/persons/${encodeURIComponent(personGuid)}/pedigrees`,
    { signal, query: { page: 0, size: 100 } },
  );
  return page.content ?? [];
}

/** The `/api/v2/pedigree` create endpoint, derived from the app's `/api/v1` base (`…/core`). */
function pedigreeV2Url(): string {
  const raw = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/core/api/v1';
  const root = raw.replace(/\/+$/, '').replace(/\/api\/v\d+$/, '');
  return `${root}/api/v2/pedigree`;
}

/**
 * Create a family-less pedigree seeded with a single proband node = the given patient's person
 * (PTV2-1148, T2). The `PedigreeDTO` requires a fresh pedigree guid (`id`), a `probandId`, and a
 * non-empty `persons` map; the proband person already exists (it is the patient), so the BE just
 * places a `pedigree_node` for it. Wire field names are camelCase — the backend configures no
 * global snake_case naming strategy and these DTO fields carry no `@JsonProperty`.
 *
 * @returns the new pedigree's business guid
 */
export async function createFamilylessPedigree(personGuid: string): Promise<string> {
  const pedigreeGuid = crypto.randomUUID();
  const body = {
    id: pedigreeGuid,
    probandId: personGuid,
    persons: {
      [personGuid]: {
        id: personGuid,
        type: 'INDIVIDUAL',
        indexNumber: 0,
      },
    },
  };
  const apiFetch = createPtv2Fetch();
  const response = await apiFetch(pedigreeV2Url(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new Error(`Failed to create pedigree (HTTP ${String(response.status)})`);
  }
  const created = (await response.json()) as { id?: string };
  return created.id ?? pedigreeGuid;
}

/**
 * Create a family around an EXISTING (floating) pedigree and attach it (PTV2-1148). Unlike the
 * `/api/v2/pedigree` create used by {@link createFamilylessPedigree}, family creation lives on the
 * app's normal `/api/v1` base, so this goes through the shared `families/api.ts` wrapper (the `http`
 * client) rather than the pedigree editor's v2 fetch. Returns the created family.
 */
export function createFamilyForPedigree({
  pedigreeGuid,
  name,
}: {
  pedigreeGuid: string;
  name: string;
}): Promise<FamilyDto> {
  return familiesApi.createFamilyForPedigree({ pedigreeGuid, name });
}

/**
 * Load just the pedigree's own name (PTV2-1157) via the light structural summary
 * (`GET /api/v2/pedigree/{guid}/summary`). Returns '' when unnamed. Used by the Legend section's
 * "Pedigree name" field in the pedigree editor.
 */
export async function fetchPedigreeName(pedigreeGuid: string): Promise<string> {
  const apiFetch = createPtv2Fetch();
  const res = await apiFetch(`${pedigreeV2Url()}/${encodeURIComponent(pedigreeGuid)}/summary`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`Failed to load pedigree summary (HTTP ${String(res.status)})`);
  }
  const summary = (await res.json()) as { pedigree_name?: string | null };
  return summary.pedigree_name ?? '';
}

/**
 * Save the pedigree's name (PTV2-1157) via the granular pedigree PATCH
 * (`PATCH /api/v2/pedigree/{guid}`, JSON-merge patch). A blank name clears it (null).
 */
export async function patchPedigreeName(pedigreeGuid: string, name: string): Promise<void> {
  const trimmed = name.trim();
  const apiFetch = createPtv2Fetch();
  const res = await apiFetch(`${pedigreeV2Url()}/${encodeURIComponent(pedigreeGuid)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ pedigree_name: trimmed === '' ? null : trimmed }),
  });
  if (!res.ok) {
    throw new Error(`Failed to save pedigree name (HTTP ${String(res.status)})`);
  }
}

/** Query factory for the patient-record Pedigree section's list of the person's pedigrees. */
export const personPedigreesQuery = (personGuid: string) =>
  queryOptions({
    queryKey: ['person', personGuid, 'pedigrees'] as const,
    queryFn: ({ signal }) => listPersonPedigrees(personGuid, signal),
    enabled: personGuid.length > 0,
    staleTime: STALE_TIME,
    // Refetch when the Pedigree section remounts — e.g. after exiting the editor, so a proband /
    // source / last-updated change made in the pedigree is reflected on the tile without a page reload.
    refetchOnMount: 'always',
  });
